import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useMediaQuery } from '@providers/MediaQueryProvider';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import {
  IResetClubEventQueryArg,
  resetClubEventsQuery,
  useInfiniteClubEventsQuery,
  useNewsQuery,
} from '@features/club/api/hooks';
import { EventListTab } from '@features/club/typings';
import { components } from '@features/shared/api/club/clients';

export type IEventsQueryCustomization = {
  tickets?: string[];
  rcfRankCalculated?: boolean;
};

export function useEventsWithPaging({
  clubId,
  type,
  subType,
  customFilter,
  customQuery,
}: {
  clubId: string;
  type: EventListTab | string;
  subType?: string;
  customFilter?: (
    events: components['schemas']['TournamentShortDto'][],
    type?: string,
    subtype?: string,
  ) => components['schemas']['TournamentShortDto'][];
  customQuery?: (type?: string, subtype?: string) => IEventsQueryCustomization | undefined;
}) {
  const qc = useQueryClient();

  const queryType = type === 'finished' ? 'finished' : 'upcoming';
  const { data: news } = useNewsQuery(clubId, 8);
  const hasNews = news.length;

  const [isShowMoreTriggered, setIsShowMoreTriggered] = useState(false);

  const isMobile = useMediaQuery() === 'mobile';
  const { isManager } = useClubRoles(clubId);

  const [query, setQuery] = useState('');

  let pageSize = 16;
  if (isMobile && queryType === 'finished') pageSize = 4;
  if (isMobile && queryType === 'upcoming') pageSize = 50;

  const { tickets, rcfRankCalculated } = customQuery?.(type, subType) || {};

  const {
    data: events,
    refetch,
    isPending,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteClubEventsQuery({
    clubId,
    type: queryType,
    isManager,
    query,
    tickets,
    rcfRankCalculated,
    pageSize,
  });

  const lastArchiveParams = useRef<IResetClubEventQueryArg>(null);

  if (queryType === 'finished') {
    lastArchiveParams.current = {
      qc,
      clubId,
      type: queryType,
      isManager,
      query,
      tickets,
      rcfRankCalculated,
    };
  }

  // Сброс show more и query (и для upcoming вкладок, и для finished)
  useEffect(() => {
    setIsShowMoreTriggered(false);
    setQuery('');
  }, [type]);

  // Сброс пейджинга (а заодно и кэша) для finished вкладок
  useEffect(() => {
    if (lastArchiveParams.current && queryType !== 'finished') {
      resetClubEventsQuery(lastArchiveParams.current);
    }
  }, [queryType]);

  const handleNextPage = useCallback(async () => {
    await fetchNextPage();
    setIsShowMoreTriggered(true);
  }, [fetchNextPage, setIsShowMoreTriggered]);

  let initialVisibleEventsCount = 12;
  if (type === 'upcoming' && hasNews) initialVisibleEventsCount = initialVisibleEventsCount - 2;
  if (isMobile) {
    initialVisibleEventsCount = type === 'finished' ? 4 : 50;
  }

  const filteredEvents = customFilter
    ? customFilter(events, type, subType)
    : queryType === 'upcoming'
      ? events.filter((e) => (type === 'drafts' ? e.status === 'Draft' : e.status !== 'Draft'))
      : events;

  // Client display limit for first showMore, so user can scroll and not die of old age
  const pagedEvents = isShowMoreTriggered
    ? filteredEvents
    : filteredEvents.slice(0, initialVisibleEventsCount);

  const neverShowLoadMore = isMobile && queryType === 'upcoming';
  const realHasNextPage =
    (hasNextPage || pagedEvents.length < filteredEvents.length) && !neverShowLoadMore;

  return {
    events: pagedEvents,
    refetch,
    isPending,
    query,
    setQuery,

    fetchNextPage: (realHasNextPage && handleNextPage) || undefined,
  };
}
